import { useNavigate } from "react-router-dom";
import { useAuth } from "../../providers/authprovider";
import { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Card, Form, InputGroup, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";

import "./Login.css";

export default function LoginPage() {
  const navigate = useNavigate();
  const { signin, verifySms } = useAuth();

  const [showPassword, setShowPassword] = useState(false);
  const togglePassword = () => setShowPassword((prev) => !prev);

  const [loading, setLoading] = useState(false);

  const [password, setPassword] = useState("");
  const [login, setLogin] = useState("");

  // --- SMS 2FA state
  const [duringSmsVerification, setDuringSmsVerification] = useState(false);
  const [smsCode, setSmsCode] = useState("");
  const [smsSecondsLeft, setSmsSecondsLeft] = useState(0);
  const smsTimerRef = useRef(null);

  const [alert, setAlert] = useState({
    show: false,
    content: "",
    variant: "",
    title: "",
  });

  const showAlert = (content, title, variant = "warning") => {
    setAlert({ show: true, content, variant, title });
  };

  const hideAlert = () => {
    setAlert({ show: false, content: "", variant: "", title: "" });
  };

  const stopSmsTimer = () => {
    if (smsTimerRef.current) {
      clearInterval(smsTimerRef.current);
      smsTimerRef.current = null;
    }
  };

  const resetSmsVerification = () => {
    stopSmsTimer();
    setDuringSmsVerification(false);
    setSmsCode("");
    setSmsSecondsLeft(0);
  };

  const startSmsCountdown = (seconds = 120) => {
    stopSmsTimer();
    setSmsSecondsLeft(seconds);

    smsTimerRef.current = setInterval(() => {
      setSmsSecondsLeft((prev) => {
        const next = prev - 1;
        if (next <= 0) {
          stopSmsTimer();
          // anuluj stan weryfikacji, login i password zostają
          resetSmsVerification();
          showAlert(
            "Czas na wpisanie kodu minął. Zaloguj się ponownie, aby wysłać nowy kod.",
            "Kod wygasł",
            "warning",
          );
          return 0;
        }
        return next;
      });
    }, 1000);
  };

  useEffect(() => {
    return () => {
      stopSmsTimer();
    };
  }, []);

  const formattedTimeLeft = useMemo(() => {
    const m = Math.floor(smsSecondsLeft / 60);
    const s = smsSecondsLeft % 60;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }, [smsSecondsLeft]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    if (name === "login") setLogin(value);
    if (name === "password") setPassword(value);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (duringSmsVerification) return;

    setLoading(true);
    hideAlert();

    signin(login, password, (result) => {
      if (result?.success) {
        resetSmsVerification();
        navigate("/", { replace: true });
      } else if (result?.verifySms) {
        // start 2FA flow'
        setDuringSmsVerification(true);
        setSmsCode("");
        startSmsCountdown(result.expiresIn);
        showAlert(
          "Wysłaliśmy kod SMS. Wpisz 6-cyfrowy kod, aby dokończyć logowanie.",
          "Weryfikacja SMS",
          "info",
        );
      } else {
        if(result?.status === 429) {
          const nextTry = result?.content?.retryAtLocal ;
          showAlert(
            `Zbyt wiele nieudanych prób logowania. Proszę spróbować ponownie później. Następna próba możliwa o ${nextTry}.`
          );
          setLoading(false);
          return;
        }
        showAlert("Wprowadzono błędny login lub hasło.", "Błędne dane", "warning");
      }

      setLoading(false);
    });
  };

  const handleSmsCodeChange = (e) => {
    // tylko cyfry, max 6
    const digitsOnly = e.target.value.replace(/\D/g, "").slice(0, 6);
    setSmsCode(digitsOnly);
  };


  // Callbackowa wersja verifySms (pewnie tak masz w authprovider)
  const verifySmsCb = (e) => {
    e.preventDefault();
    if (!duringSmsVerification) return;

    hideAlert();

    const code = smsCode.trim();
    if (code.length !== 6) {
      showAlert("Kod musi mieć 6 cyfr.", "Niepoprawny kod", "warning");
      return;
    }

    setLoading(true);

    verifySms(login, code, (result) => {
      if (result?.success) {
        resetSmsVerification();
        navigate("/", { replace: true });
      } else {
        showAlert("Kod jest nieprawidłowy lub wygasł.", "Błędny kod", "warning");
      }
      setLoading(false);
    });
  };

  const cancelSms = () => {
    resetSmsVerification();
    hideAlert();
  };

  return (
    <div className="login-wrapper d-flex align-items-center justify-content-center min-vh-100 px-3">
      <Card className="p-4 w-100" style={{ maxWidth: "420px" }}>
        <img
          src="/logo.png"
          alt="Control System Instalacje"
          className="mx-auto d-block mb-4"
          style={{ maxWidth: "160px" }}
        />

        <Form onSubmit={handleSubmit}>
          {alert.show && (
            <Alert
              show={alert.show}
              variant={alert.variant}
              onClose={hideAlert}
              dismissible
            >
              <p className="mb-0">{alert.content}</p>
            </Alert>
          )}

          <Form.Group controlId="email" className="mb-3">
            <Form.Label>Login</Form.Label>
            <Form.Control
              autoFocus
              type="text"
              name="login"
              value={login}
              onChange={handleInputChange}
              disabled={duringSmsVerification || loading}
            />
          </Form.Group>

          <Form.Group controlId="password" className="mb-3">
            <Form.Label>Hasło</Form.Label>
            <InputGroup>
              <Form.Control
                type={showPassword ? "text" : "password"}
                name="password"
                value={password}
                onChange={handleInputChange}
                disabled={duringSmsVerification || loading}
              />
              <InputGroup.Text
                onClick={togglePassword}
                style={{ cursor: "pointer", userSelect: "none" }}
              >
                <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
              </InputGroup.Text>
            </InputGroup>
          </Form.Group>

          {/* --- SMS 2FA UI */}
          {duringSmsVerification ? (
            <div className="mt-3">
              <Form.Group controlId="smsCode" className="mb-2">
                <Form.Label>Kod SMS (6 cyfr)</Form.Label>
                <Form.Control
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="______"
                  value={smsCode}
                  onChange={handleSmsCodeChange}
                  disabled={loading}
                />
                <div className="text-muted mt-1" style={{ fontSize: 13 }}>
                  Pozostały czas: <strong>{formattedTimeLeft}</strong>
                </div>
              </Form.Group>

              <div className="d-flex gap-2">
                <Button
                  variant="primary"
                  className="w-100"
                  onClick={verifySmsCb}
                  disabled={loading || smsCode.length !== 6 || smsSecondsLeft <= 0}
                >
                  {loading ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Weryfikuję...
                    </>
                  ) : (
                    "Potwierdź kod"
                  )}
                </Button>
                <Button variant="outline-secondary" onClick={cancelSms} disabled={loading}>
                  Anuluj
                </Button>
              </div>
            </div>
          ) : loading ? (
            <div className="text-center mt-4">
              <FontAwesomeIcon icon={faSpinner} spin size="2x" />
            </div>
          ) : (
            <Button variant="primary" type="submit" size="lg" className="w-100 mt-3">
              Zaloguj się
            </Button>
          )}
        </Form>
      </Card>
    </div>
  );
}
